import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Sparkles, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import logoLight from "@/assets/logo-light.png";

const Join = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { user } = useAuth();
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [validCode, setValidCode] = useState(false);

  useEffect(() => {
    const validateCode = async () => {
      if (!code) {
        setLoading(false);
        return;
      }

      // Check if code is valid using secure function (works without authentication)
      const { data, error } = await supabase
        .rpc("get_referrer_by_code", { _code: code });

      if (data && data.length > 0 && !error) {
        const referrer = data[0];
        setReferrerName(referrer.username || (locale.language === "pt" ? "Um amigo" : "A friend"));
        setValidCode(true);
        // Store referral code for signup
        sessionStorage.setItem("referralCode", code);
        sessionStorage.setItem("referrerId", referrer.user_id);
      }

      setLoading(false);
    };

    validateCode();
  }, [code, locale.language]);

  // If user is already logged in, redirect to home
  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleSignUp = () => {
    navigate("/auth");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">
          {locale.language === "pt" ? "Carregando..." : "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/20 rounded-full blur-[100px] animate-float pointer-events-none" />
      <div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px] animate-float pointer-events-none"
        style={{ animationDelay: "-3s" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="glass-card p-8 text-center">
          {/* Logo */}
          <img src={logoLight} alt="Callyout" className="h-10 mx-auto mb-6" />

          {validCode ? (
            <>
              {/* Invitation badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 border border-success/30 text-success mb-6">
                <Users size={18} />
                <span className="font-medium">
                  {locale.language === "pt" ? "Convite especial!" : "Special invitation!"}
                </span>
              </div>

              <h1 className="text-2xl font-bold text-foreground mb-2">
                {locale.language === "pt"
                  ? `${referrerName} convidou você!`
                  : `${referrerName} invited you!`}
              </h1>

              <p className="text-muted-foreground mb-6">
                {locale.language === "pt"
                  ? "Crie sua conta e comece a fazer previsões no Callyout. Você ganha 100 CallyCoins para começar!"
                  : "Create your account and start making predictions on Callyout. You get 100 CallyCoins to start!"}
              </p>

              <div className="flex items-center justify-center gap-2 mb-6 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <Sparkles size={20} className="text-primary" />
                <span className="text-primary font-semibold">
                  {locale.language === "pt" ? "100 CallyCoins grátis!" : "100 Free CallyCoins!"}
                </span>
              </div>

              <Button variant="hero" className="w-full" onClick={handleSignUp}>
                {locale.language === "pt" ? "Criar conta" : "Create Account"}
                <ArrowRight size={18} />
              </Button>

              <p className="text-xs text-muted-foreground mt-4">
                {locale.language === "pt"
                  ? "Ao se cadastrar, você concorda com nossos Termos de Serviço."
                  : "By signing up, you agree to our Terms of Service."}
              </p>
            </>
          ) : (
            <>
              {/* Invalid code */}
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {locale.language === "pt" ? "Link inválido" : "Invalid Link"}
              </h1>

              <p className="text-muted-foreground mb-6">
                {locale.language === "pt"
                  ? "Este link de convite não é válido ou expirou."
                  : "This invitation link is invalid or has expired."}
              </p>

              <Button variant="hero" className="w-full" onClick={() => navigate("/auth")}>
                {locale.language === "pt" ? "Ir para cadastro" : "Go to Sign Up"}
                <ArrowRight size={18} />
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Join;
